import { forwardRef, useEffect, useState } from 'react';
import { InputValue, Position, Sized, useComponentProps } from '@optimus-web/common';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { ErrorIcon, MaxCharacterCount } from '../common/index';
import { Spinner } from '../Spinner';
import { Tooltip } from '../Tooltip';
import { ensureString } from '../utils';
import {
    IndicativeIconWrapper,
    InputContainer,
    InputElement,
    InputWrapper,
    LeftSide,
    PlaceholderElement,
    Prefix,
    RightSide,
    Suffix,
} from './style';

const TooltippedErrorIcon = Tooltip(ErrorIcon);

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder' | 'onChange' | 'prefix'>,
        Sized {
    alignRight?: boolean;
    clearable?: boolean;
    error?: React.ReactNode;
    getValue?: (value: string) => void;
    id?: string;
    indicativeIcon?: IconName;
    inline?: boolean;
    interactiveIcon?: IconName;
    interactiveIconHandler?: () => void;
    interactiveIconRef?: (ref: HTMLButtonElement | null) => void;
    interactiveIconExtraProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
    accessibleIconLabel?: React.ReactChild;
    leadingImage?: React.ReactNode;
    loading?: boolean;
    maxCharacterCount?: number;
    name: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => void;
    onClear?: () => void;
    placeholder?: React.ReactNode;
    prefix?: React.ReactNode;
    readOnly?: boolean;
    required?: boolean;
    suffix?: React.ReactNode;
    trailingImage?: React.ReactNode;
    value?: InputValue;
    keyboardEntryDisabled?: boolean;
}

const Input = forwardRef(
    (
        {
            alignRight,
            clearable,
            disabled,
            error,
            getValue,
            id,
            indicativeIcon,
            inline,
            interactiveIcon,
            interactiveIconHandler,
            interactiveIconRef,
            accessibleIconLabel,
            leadingImage,
            loading,
            maxCharacterCount,
            name,
            onChange,
            onClear,
            placeholder,
            prefix,
            readOnly,
            required,
            size: customSize,
            suffix,
            trailingImage,
            value,
            keyboardEntryDisabled,
            interactiveIconExtraProps = {},
            ...other
        }: InputProps,
        ref: React.Ref<HTMLInputElement>
    ) => {
        const [ranOnce, setRanOnce] = useState(false);
        const { size } = useComponentProps('Input', { size: customSize });
        const { t } = useLocalization();

        useEffect(() => {
            if (!value && !ranOnce) {
                setRanOnce(true);
            } else if (!ranOnce && getValue && value) {
                getValue(value.toString());
                setRanOnce(true);
            }
        }, [getValue, ranOnce, value]);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(event, event.target.value);
            }
            if (getValue) {
                getValue(event.target.value.toString());
            }
        };

        const handleClearInput = (event: React.MouseEvent) => {
            if (getValue) {
                getValue('');
            }

            if (onClear) {
                onClear();
            } else if (onChange) {
                onChange(event, '');
            }
        };

        const hasRightSide =
            !!interactiveIcon || clearable || !!suffix || loading || !!trailingImage || !!maxCharacterCount || !!error;
        const hasLeftSide = !!indicativeIcon || !!prefix || !!leadingImage;
        const valueIsFalsy = value == null || value === '';

        return (
            <InputContainer readOnly={readOnly} inputSize={size} disabled={disabled}>
                {hasLeftSide ? (
                    <LeftSide inputSize={size} spacing="50" itemAlignment="center">
                        {indicativeIcon ? (
                            <IndicativeIconWrapper
                                size="small"
                                value={indicativeIcon}
                                data-test-indicative-icon
                                variant="inherit"
                                disabled={disabled}
                            />
                        ) : null}
                        {leadingImage}
                        {prefix ? (
                            <Prefix $disabled={disabled} data-test-prefix>
                                {prefix}
                            </Prefix>
                        ) : null}
                    </LeftSide>
                ) : null}

                <InputWrapper>
                    {valueIsFalsy && placeholder ? (
                        <PlaceholderElement
                            $readOnly={readOnly}
                            inputSize={size}
                            disabled={disabled}
                            hasRightSide={hasRightSide}
                            hasLeftSide={hasLeftSide}
                            data-test-id="ds-placeholder-element"
                        >
                            {placeholder}
                        </PlaceholderElement>
                    ) : null}
                    <InputElement
                        readOnly={readOnly}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        inputSize={size}
                        disabled={disabled}
                        ref={ref}
                        alignRight={alignRight}
                        {...other}
                        id={name}
                        hasRightSide={hasRightSide}
                        hasLeftSide={hasLeftSide}
                        aria-required={required}
                        required={required}
                        {...(name && error ? { 'aria-errormessage': `${name}-error` } : {})}
                        aria-invalid={!!error}
                        keyboardEntryDisabled={keyboardEntryDisabled}
                    />
                </InputWrapper>

                {hasRightSide ? (
                    <RightSide inputSize={size} spacing="100" itemAlignment="center">
                        {inline && !!maxCharacterCount ? (
                            <MaxCharacterCount
                                inline
                                inputValueLength={value?.toString().length || 0}
                                maxCharacterCount={maxCharacterCount}
                                disabled={disabled}
                            />
                        ) : null}
                        {suffix ? (
                            <Suffix $disabled={disabled} data-test-suffix>
                                {suffix}
                            </Suffix>
                        ) : null}
                        {clearable && value ? (
                            <BareIconButton
                                size="small"
                                icon={IconName.CrossClose}
                                onClick={handleClearInput}
                                readOnly={readOnly}
                                disabled={disabled}
                                aria-label={ensureString({ value: t(OptimusTextKey.ClearSelection) })}
                                data-test-button-clear
                            />
                        ) : null}
                        {loading ? <Spinner size="small" data-test-loader /> : null}
                        {trailingImage}
                        {interactiveIcon ? (
                            <BareIconButton
                                size="small"
                                icon={interactiveIcon}
                                accessibleText={accessibleIconLabel}
                                onClick={interactiveIconHandler}
                                readOnly={readOnly}
                                disabled={disabled}
                                aria-label={ensureString({ value: accessibleIconLabel })}
                                data-test-icon-interactive
                                ref={interactiveIconRef}
                                {...interactiveIconExtraProps}
                            />
                        ) : null}
                        {inline && error ? (
                            <TooltippedErrorIcon tooltip={error} tooltipPosition={Position.Right} />
                        ) : null}
                    </RightSide>
                ) : null}
            </InputContainer>
        );
    }
);

export default Input;
